"use client";

import type { TeamAgent } from "@bash/shared";
import type { ComposerHistoryEntry } from "@/stores/composerHistoryStore";
import {
  cardWords,
  toolWords,
} from "@/components/home-briefing/composerHistoryRows";
import { IntegrationIcon } from "@/components/ui/IntegrationIcons";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { AgentFace } from "@/components/team/AgentFace";
import { BashFace } from "../BashFace";
import { CopyButton } from "./CopyButton";
import { FoldedAnswer } from "./FoldedAnswer";
import { Glyph } from "./glyphs";
import { HistoryTime } from "./HistoryTime";
import { STATUS_LABEL, TaskCard } from "./TaskCard";

type Receipt = NonNullable<ComposerHistoryEntry["receipt"]>;

/** Bash's own words on the row: the whole sentence when he spoke first, the
 *  reply for an answer or a run, else the words he said beside the action. */
function wordsOf(e: ComposerHistoryEntry): string | undefined {
  if (e.author === "bash" || e.kind === "answered" || e.kind === "run")
    return e.text;
  return e.reply;
}

/** The card a run used, in plain words ("gmail: send email"), else the tools. */
function ranWords(tools: string[] | undefined): string | null {
  const id = [...(tools ?? [])].reverse().find((t) => cardWords(t) !== null);
  return id ? cardWords(id) : toolWords(tools);
}

/** The toolkit behind a run's card (app.<toolkit>.<SLUG>), for its own icon. */
function toolkitOf(tools: string[] | undefined): string | null {
  for (const t of [...(tools ?? [])].reverse()) {
    const m = /^app\.([^.\s]+)\./.exec(t);
    if (m) return m[1]!;
  }
  return null;
}

const receiptWords = (r: Receipt): string =>
  r.meta ? `${r.title}: ${r.meta}` : r.title;

/** What Copy on Bash's turn takes: his words, then what the ask became. */
function turnCopy(e: ComposerHistoryEntry): string {
  const parts: string[] = [];
  const words = wordsOf(e);
  if (words) parts.push(words);
  const title = e.task?.title || e.text;
  if (e.kind === "run" && e.receipt) parts.push(receiptWords(e.receipt));
  if (e.kind === "task_created") {
    const status = e.task?.status;
    parts.push(
      status
        ? `${title}\n${STATUS_LABEL[status]}${e.task?.line ? `: ${e.task.line}` : ""}`
        : title
    );
  }
  if (e.kind === "task_followup") parts.push(`Added to ${title}`);
  if (e.kind === "nami_saved")
    parts.push(e.text ? `Saved to memory: ${e.text}` : "Saved to memory");
  return parts.join("\n\n");
}

function ReceiptBlock({
  receipt,
  toolkit,
}: {
  receipt: Receipt;
  toolkit: string | null;
}) {
  return (
    <div
      className="grid w-full max-w-115 grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded-12 border border-border-subtle bg-bg-subtle py-2 pr-3 pl-2"
      data-testid="history-receipt"
    >
      <span
        className="grid size-8 place-items-center rounded-8 border border-border-default bg-bg-elevated text-fg-default"
        aria-hidden="true"
      >
        {toolkit ? (
          <IntegrationIcon integration={toolkit} size={20} />
        ) : (
          <Glyph name="action" />
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-3.5 font-medium text-fg-default">
          {receipt.title}
        </span>
        {receipt.meta && (
          <span className="truncate text-3 text-fg-muted">{receipt.meta}</span>
        )}
      </span>
      <span className="grid text-kind-green" title="Done">
        <CheckIcon aria-hidden="true" strokeWidth={2.4} className="size-4" />
      </span>
    </div>
  );
}

function MemoryChip({ summary }: { summary: string }) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5"
      data-testid="history-memory"
    >
      <span className="inline-flex h-6.5 items-center gap-1.5 rounded-full bg-nami-700-14 pr-3 pl-2 text-3 font-medium text-nami-700">
        <Glyph name="nami" className="size-3.5" />
        Saved to memory
      </span>
      {summary && <span className="text-3 text-fg-muted">{summary}</span>}
    </div>
  );
}

/** An app opened or the tasks shown: one quiet line, no face. */
function QuietLine({
  entry,
  onGo,
}: {
  entry: ComposerHistoryEntry;
  onGo: (route: string) => void;
}) {
  const route = entry.route;
  return (
    <div
      className="grid grid-cols-[24px_minmax(0,1fr)] items-center gap-x-3 text-3 text-fg-subtle"
      data-testid="history-quiet"
    >
      <Glyph
        name="open"
        className="size-3.5 justify-self-center text-fg-faint"
      />
      <span className="min-w-0">
        {entry.kind === "show_tasks" ? (
          "Showed your tasks"
        ) : (
          <>
            Opened{" "}
            {route ? (
              <button
                type="button"
                onClick={() => onGo(route)}
                className="cursor-pointer border-0 bg-transparent p-0 font-medium text-fg-muted underline-offset-2 hover:text-fg-default hover:underline"
              >
                {entry.text}
              </button>
            ) : (
              <span className="font-medium text-fg-muted">{entry.text}</span>
            )}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Bash's side of an exchange, by what the ask became (spec 2026-09-24 §1):
 * the small face on the left, his words as markdown, then the task card, the
 * action receipt or the memory chip, and Copy. An app opened or the tasks
 * shown is a quiet line instead. `showTime` when he spoke first: the row's
 * one time has no bubble of his to sit under. With a `speaker` (an agent's
 * transcript, train 1b) the turn is that agent's: its kin face and its name
 * over the words; without one it is Bash's, exactly as before.
 */
export function BashTurn({
  entry,
  onGo,
  showTime = false,
  speaker,
}: {
  entry: ComposerHistoryEntry;
  onGo: (route: string) => void;
  showTime?: boolean;
  speaker?: Pick<TeamAgent, "id" | "kind" | "name">;
}) {
  if (entry.kind === "app_control" || entry.kind === "show_tasks")
    return <QuietLine entry={entry} onGo={onGo} />;

  const words = wordsOf(entry);
  const receipt = entry.kind === "run" ? entry.receipt : undefined;
  const ran = entry.kind === "run" && !receipt ? ranWords(entry.tools) : null;
  const taskId = entry.taskId;
  const openTask = taskId ? () => onGo(`/task/${taskId}`) : undefined;
  const copy = turnCopy(entry);

  return (
    <div
      className="group/turn grid grid-cols-[24px_minmax(0,1fr)] items-start gap-x-3"
      data-testid="history-bash-turn"
    >
      {/* The speaker's own face, still: a column of turns never blinks in unison. */}
      {speaker ? (
        <AgentFace
          agent={speaker}
          size="chip"
          className="[&_.bh-face\_\_eye]:animate-none"
        />
      ) : (
        <BashFace size="nav" className="[&_.bh-face\_\_eye]:animate-none" />
      )}
      <div className="flex min-w-0 flex-col items-start gap-2">
        {speaker && (
          <span
            className="text-3 leading-6 font-medium text-fg-muted"
            data-testid="history-speaker"
          >
            {speaker.name}
          </span>
        )}
        {words && <FoldedAnswer markdown={words} />}
        {ran && (
          <p
            className="m-0 font-mono text-3 text-fg-subtle"
            data-testid="history-ran"
          >
            {ran}
          </p>
        )}
        {receipt && (
          <ReceiptBlock receipt={receipt} toolkit={toolkitOf(entry.tools)} />
        )}
        {entry.kind === "task_created" && (
          <TaskCard entry={entry} onOpen={openTask} />
        )}
        {entry.kind === "task_followup" && (
          <TaskCard entry={entry} slim onOpen={openTask} />
        )}
        {entry.kind === "nami_saved" && <MemoryChip summary={entry.text} />}
        <div className="-mt-1 flex h-5.5 items-center gap-1 text-3 text-fg-subtle">
          {showTime && <HistoryTime ts={entry.ts} />}
          {copy && <CopyButton text={copy} label="Copy reply" />}
        </div>
      </div>
    </div>
  );
}
